<template>
  <DsSection>
    <div class="ds-grid ds-margin-bottom-24 ds-md-margin-bottom-48">
      <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--10 ds-md-grid__col--offset-1">
        <DsEyebrow
          :text="eyebrow"
          class="ds-justify-content-center"
        />
        <h1 class="ds-heading ds-md-heading-large ds-text-align-center">
          {{ title }}
        </h1>
      </div>
    </div>
    <div
      class="ds-grid"
      :class="{ 'ds-justify-content-space-between': relatedDocuments.length > 0 }"
    >
      <!-- Description -->
      <RichText
        :data="description"
        class="ds-grid__col ds-grid__col--12"
        :class="{
          'ds-md-grid__col--6 ds-md-grid__col--offset-3 ds-md-grid__col--no-gutter ds-text-align-center':
            relatedDocuments.length === 0,
          'ds-md-grid__col--7': relatedDocuments.length > 0
        }"
      />
      <!-- Documents -->
      <div
        v-if="relatedDocuments.length > 0"
        class="ds-grid__col ds-grid__col--12 ds-md-grid__col--4 ds-margin-top-24 ds-md-margin-top-0"
      >
        <h3 class="ds-heading-xsmall ds-margin-bottom-24">
          {{ labels.documentsTitle || 'Documents' }}
        </h3>
        <div class="ds-display-flex ds-flex-direction-column">
          <DsLink
            v-for="(link, index) in relatedDocuments"
            :key="`document-link-${index}`"
            :url="link.url"
            :open-in-new-window="link.openInNewWindow"
            :nuxt="link.nuxt"
            :download="link.download"
            :content="link.title"
            class="documents-list-item"
            color="black-green"
            variant="quaternary"
          />
        </div>
      </div>
    </div>
    <!--  Image  -->
    <DsSection
      v-if="showImage && hasImageSrc"
      :with-space="false"
      class="ds-margin-top-24 ds-md-margin-top-48"
    >
      <DsImageElement
        class="image-element"
        :image="image"
        :screen="screen"
      />
    </DsSection>
    <!--  Video  -->
    <DsSection
      v-if="showVideo"
      :with-space="false"
      class="ds-margin-top-24 ds-md-margin-top-48"
    >
      <DsScreenWrapper
        v-if="video"
        :ready="hydrateReady"
      >
        <DsVideoPlayer
          :aria-label-play="labelPlay"
          :aria-label-close="labels.close"
          v-bind="video"
          :screen="screen"
        />
      </DsScreenWrapper>
    </DsSection>
    <!--  Livestream  -->
    <DsSection
      v-if="showLivestream && liveStreamVideo.link"
      :with-space="false"
      class="ds-margin-top-24 ds-md-margin-top-48 livestream-container"
    >
      <iframe
        width="100%"
        height="100%"
        frameborder="0"
        :src="liveStreamVideo.link"
        :title="liveStreamVideo.title"
        allow="accelerometer; autoplay; encrypted-media; clipboard-write; gyroscope; picture-in-picture"
        allowfullscreen
        class="livestream-container__iframe"
      />
    </DsSection>
  </DsSection>
</template>

<script setup>
import { DsImageElement, DsLink, DsScreenWrapper, DsSection, DsVideoPlayer, DsEyebrow } from 'awds'
const { screen } = useScreen()
const { localeGlobalContentData, labels } = useGlobalData()
const { hydrateReady } = useHydrateHook()

const props = defineProps({
  title: { type: String, required: true },
  eyebrow: { type: String, required: true },
  description: { type: Object, required: true },
  relatedDocuments: { type: Array, default: () => [] },
  image: { type: Object, default: null },
  video: { type: Object, default: null },
  liveStreamVideo: { type: Object, default: null },
  showLivestream: { type: Boolean, required: true },
  showVideo: { type: Boolean, required: true },
  showImage: { type: Boolean, required: true }
})

const hasImageSrc = computed(() => {
  return !!props.image?.medium?.src
})

const labelPlay = computed(() => {
  return labels.value.play
})
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';
//@import 'awds/foundation/mixins/utilities/_multimedia.mixins.scss';

//.livestream-container {
//  @include multimedia-container-ratio('fixed');
//
//  &__iframe {
//    border: $spacing-0;
//  }
//}

// Force Live Stream container height based on image sizes: IMAGE_SIZES_COLUMNS_12_169

.livestream-container {
  :deep() .ds-container {
    height: 184px;
  }
}

@include screen('medium') {
  .livestream-container {
    :deep() .ds-container {
      height: 378px;
    }
  }
}

@include screen('large') {
  .livestream-container {
    :deep() .ds-container {
      height: 675px;
    }
  }
}

.documents-list-item {
  margin-bottom: $spacing-12;

  &:last-child {
    margin-bottom: $spacing-0;
  }
}
</style>
